/** Chat data hooks and the live stream (server-sent events) that keeps them fresh. */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useSyncExternalStore } from "react";
import { api } from "./api";

export interface Conversation {
  id: string;
  kind: "channel" | "direct" | "group" | "task";
  name: string | null;
  teamId: string | null;
  companyId: string | null;
  itemId: string | null;
  lastMessageAt: string | null;
  muted: boolean;
  unread: number;
  last: { authorName: string; body: string; at: string } | null;
  members: Array<{ id: string; name: string }> | null;
  memberCount: number;
  item: { id: string; ref: string; title: string; state: string } | null;
}
export interface ChatFile { id: string; name: string; type: string; size: number }
export interface Reaction { emoji: string; count: number; mine: boolean; names: string[] }
export interface TaskCard { id: string; ref: string; title: string; state: string; statusLabel: string | null; dueAt: string | null; ownerName: string | null; assigneeNames: string[] | null; threadCount: number; threadLastAt: string | null }
export interface Message {
  id: string;
  conversationId: string;
  parentId: string | null;
  authorId: string;
  authorName: string;
  body: string;
  mentions: string[];
  createdAt: string;
  editedAt: string | null;
  deleted: boolean;
  pinned: boolean;
  files: ChatFile[];
  reactions: Reaction[];
  replyCount: number;
  lastReplyAt: string | null;
  item: TaskCard | null;
  /** names of members who read past this message (not the author) */
  seenBy: string[];
  /** kept on this phone, not sent yet */
  pending?: boolean;
}

// ── typing and presence: tiny stores fed by the stream ───────────────────────
type Typing = Record<string, Record<string, { name: string; until: number }>>; // conversation → person → until
let typing: Typing = {};
let presence: Record<string, boolean> = {};
const subs = new Set<() => void>();
const ping = () => subs.forEach((l) => l());
const sub = (l: () => void) => { subs.add(l); return () => { subs.delete(l); }; };
export function useTyping(conversationId: string): string[] {
  const t = useSyncExternalStore(sub, () => typing);
  const now = Date.now();
  return Object.values(t[conversationId] ?? {}).filter((x) => x.until > now).map((x) => x.name);
}
export function usePresence(): Record<string, boolean> {
  return useSyncExternalStore(sub, () => presence);
}
export function setPresence(ids: Array<{ id: string; online: boolean }>) {
  presence = { ...presence, ...Object.fromEntries(ids.map((x) => [x.id, x.online])) };
  ping();
}
// ── in-app notice when a message arrives while you're elsewhere in ONE ───────
export interface Toast { id: string; conversationId: string; title: string; body: string; at: number }
let toasts: Toast[] = [];
let activeConversation: string | null = null;
export const setActiveConversation = (id: string | null) => { activeConversation = id; };
export function useToasts(): Toast[] { return useSyncExternalStore(sub, () => toasts); }
export function dismissToast(id: string) { toasts = toasts.filter((t) => t.id !== id); ping(); }
export const soundOn = () => { try { return localStorage.getItem("one.chat.sound") !== "off"; } catch { return true; } };
export const setSoundOn = (on: boolean) => { try { localStorage.setItem("one.chat.sound", on ? "on" : "off"); } catch { /* private window */ } ping(); };
export function useSoundOn() { return useSyncExternalStore(sub, soundOn); }
/** A short two-note chime made on the spot (no audio file to cache). */
export function chime() {
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const play = (freq: number, at: number) => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sine"; o.frequency.value = freq; g.gain.setValueAtTime(0.0001, at); g.gain.exponentialRampToValueAtTime(0.18, at + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, at + 0.35); o.connect(g).connect(ctx.destination); o.start(at); o.stop(at + 0.4); };
    play(880, ctx.currentTime); play(1175, ctx.currentTime + 0.16);
    setTimeout(() => void ctx.close(), 1000);
  } catch { /* no audio here */ }
}

/** Who's online among the people I chat with; refreshed now and then, and live through the stream. */
export const usePresenceList = () => useQuery({ queryKey: ["chat", "presence"], queryFn: () => api<{ items: Array<{ id: string; online: boolean; lastSeenAt: string | null }> }>("/api/chat/presence"), refetchInterval: 60_000 });

export const useConversations = () => useQuery({ queryKey: ["chat", "conversations"], queryFn: () => api<{ items: Conversation[]; me: string; emoji: string[] }>("/api/chat/conversations"), refetchInterval: 120_000 });
export const useChatUnread = () => useQuery({ queryKey: ["chat", "unread"], queryFn: () => api<{ unread: number }>("/api/chat/unread"), refetchInterval: 90_000 });

/** What a direct or group chat is called for me: the other people's names. */
export function conversationTitle(c: Conversation, me: string): string {
  if (c.kind === "channel") return c.name ?? "Channel";
  if (c.kind === "task") return c.item ? `${c.item.ref.replace(/^WRK-\d{4}-0*/, "WRK-")} ${c.item.title}` : "Task";
  if (c.name) return c.name;
  const others = (c.members ?? []).filter((m) => m.id !== me).map((m) => m.name);
  return others.length ? others.join(", ") : "Just you";
}

/**
 * One event stream per open tab. Events are pointers (which conversation, which message); the page refetches.
 * Reconnects by itself (EventSource does), and a tab in the background keeps it so the badge stays right.
 */
export function useChatStream(enabled: boolean) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!enabled || typeof EventSource === "undefined") return;
    const es = new EventSource("/api/chat/stream", { withCredentials: true });
    const bump = (e: MessageEvent) => {
      let d: { conversationId?: string } = {};
      try { d = JSON.parse(e.data); } catch { /* pointer only */ }
      if (d.conversationId) void qc.invalidateQueries({ queryKey: ["chat", "messages", d.conversationId] });
      void qc.invalidateQueries({ queryKey: ["chat", "conversations"] });
      void qc.invalidateQueries({ queryKey: ["chat", "unread"] });
      void qc.invalidateQueries({ queryKey: ["inbox"] });
    };
    for (const t of ["message", "edited", "deleted", "read"]) es.addEventListener(t, bump as EventListener);
    // WhatsApp rule: a message in any chat I'm in sounds and shows a notice, unless I'm looking at that chat or muted it
    es.addEventListener("message", ((e: MessageEvent) => {
      let d: { conversationId?: string; messageId?: string } = {};
      try { d = JSON.parse(e.data); } catch { return; }
      if (!d.messageId || !d.conversationId) return;
      if (activeConversation === d.conversationId && document.visibilityState === "visible") return;
      void api<Message & { conversation: { id: string; kind: string; name: string | null; muted: boolean } }>(`/api/chat/messages/${d.messageId}`).then((m) => {
        if (m.conversation.muted || m.deleted) return;
        const mine = (() => { try { return JSON.parse(sessionStorage.getItem("one.me") ?? "null") as string | null; } catch { return null; } })();
        if (mine && m.authorId === mine) return;
        const where = m.conversation.kind === "channel" ? `#${m.conversation.name}` : m.conversation.kind === "task" ? m.conversation.name ?? "Task thread" : m.conversation.kind === "group" ? m.conversation.name ?? "Group" : "";
        toasts = [...toasts.slice(-3), { id: m.id, conversationId: m.conversationId, title: where ? `${m.authorName} · ${where}` : m.authorName, body: m.item && !m.body ? "shared a task" : m.body ? plainBody(m.body).slice(0, 140) : "sent a file", at: Date.now() }];
        ping();
        setTimeout(() => dismissToast(m.id), 8000);
        if (soundOn()) chime();
      }).catch(() => undefined);
    }) as EventListener);
    es.addEventListener("typing", ((e: MessageEvent) => {
      try {
        const d = JSON.parse(e.data) as { conversationId: string; personId: string; name: string };
        typing = { ...typing, [d.conversationId]: { ...(typing[d.conversationId] ?? {}), [d.personId]: { name: d.name, until: Date.now() + 4000 } } };
        ping();
        setTimeout(ping, 4100);
      } catch { /* ignore */ }
    }) as EventListener);
    es.addEventListener("presence", ((e: MessageEvent) => {
      try { const d = JSON.parse(e.data) as { personId: string; online: boolean }; setPresence([{ id: d.personId, online: d.online }]); } catch { /* ignore */ }
    }) as EventListener);
    return () => es.close();
  }, [enabled, qc]);
}

export const MENTION_RE = /@\[([^\]]+)\]\(([0-9a-f-]{36})\)/g;
/** Plain-text preview of a message (mentions become @Name). */
export const plainBody = (b: string) => b.replace(MENTION_RE, "@$1");
